import React, { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { HeaderMfe } from '|/app/header';
import SlateHeader from '|/app/header/slate-header';
import CancelOrderModalListener from '|/app/purchase/checkout/cancel-order-modal-listener';
import { setChatPageContext } from '|/app/purchase/checkout/constants/chat-page-context';
import { getChatSurface } from '|/app/purchase/checkout/constants/chat-surface-mappings';
import EditOrderModalListener from '|/app/purchase/checkout/edit-order-modal-listener';
import OpdMfeRoute from '|/app/purchase/flow/mfe-routes/opd-mfe-route';
import { Step } from '|/app/services';
import { useBrand, useBrandCapabilities } from '|/brand';
import { FormSuspense } from '|/context';
import { AppFrameworkContextProvider } from '|/helpers/app-framework';
import { useOrderPlacedRouteTelemetry } from '|/hooks/use-order-placed-route-telemetry';
import { InsuranceQuoteRoute } from './insurance-quote/route';
import stepLayoutStyles from '../shared/step-layout.module.css';

interface OrderPlacedRouteProps {
  steps?: Step[];
}

const OrderPlacedHeader = () => {
  const { brand } = useBrand();
  return brand === 'slate' ? <SlateHeader /> : <HeaderMfe orderSummary={null} />;
};

// One route for every path the MFE serves, so its own redirects never remount it.
const OrderPlacedDashboard = () => {
  useOrderPlacedRouteTelemetry('opd-mfe');

  return (
    <div className={stepLayoutStyles.opdLayout}>
      <OrderPlacedHeader />
      <OpdMfeRoute />
      {/* OPD v2 fires core-events to open these shell-owned modals; mount the listeners
          here since this layout bypasses CheckoutPage / the nav-rail that host them. */}
      <CancelOrderModalListener />
      <EditOrderModalListener />
    </div>
  );
};

const OrderPlacedInsuranceQuote = () => {
  useOrderPlacedRouteTelemetry('insurance-quote');

  return (
    <>
      <OrderPlacedHeader />
      {/* Keeps the header in place while the quote's chunk loads. */}
      <AppFrameworkContextProvider>
        <FormSuspense>
          <div className={stepLayoutStyles.experimentOrderPlacedLayoutContainer}>
            <InsuranceQuoteRoute />
          </div>
        </FormSuspense>
      </AppFrameworkContextProvider>
    </>
  );
};

export const OrderPlacedRoute: React.FC<OrderPlacedRouteProps> = () => {
  const { brand } = useBrand();
  const { navigation } = useBrandCapabilities();

  useEffect(() => {
    const surface = getChatSurface('order-placed', brand);
    if (surface) setChatPageContext(surface);
  }, [brand]);

  return (
    <Routes>
      {navigation.servesOrderPlacedInsuranceQuote && (
        <Route path="insurance-quote/*" element={<OrderPlacedInsuranceQuote />} />
      )}
      <Route path="*" element={<OrderPlacedDashboard />} />
    </Routes>
  );
};
